/* eslint-disable max-statements */
import * as store from '@/store';
import KuAuthMap, {AuthMapType, AuthObjType} from './KuAuthMap';
import {getDepartmentInfo, getDocumentInfo, getRepositoryInfo} from '@/services';
import {getPageParams} from '@/utils/commonUtils';
import {AuthItem, AuthFnParams, AuthType, AuthResultType} from './auth.td';

const KU_AUTH_TYPE: any = {
    department: {
        storeKey: store.KEYS.CUR_DEPARTMENT,
        errorKey: store.KEYS.CUR_DEPARTMENT_ERROR,
        roleName: 'roleName',
        status: 'status',
        groupType: 'groupType',
    },
    repository: {
        storeKey: store.KEYS.CUR_REPOSITORY,
        errorKey: store.KEYS.CUR_REPOSITORY_ERROR,
        roleName: 'roleName',
        status: 'repoStatus',
        ownerType: 'ownerType',
        sourceType: 'sourceType',
        scope: 'scope',
    },
    document: {
        storeKey: store.KEYS.CUR_DOCUMENT,
        errorKey: store.KEYS.CUR_DOCUMENT_ERROR,
        roleName: 'docRoleName',
        repositoryRoleName: 'repositoryRoleName',
        status: 'docStatus',
        scope: 'scope',
        type: 'type',
    },
};

// 那些有大小关系的权限列表，每一类从前到后权限越来越大【必须】！！！
const KU_AUTH_LIST: any = {
    department: {
        roleName: ['Member', 'Admin', 'AAOwner'],
    },
    repository: {
        roleName: ['Reader', 'Member', 'Admin', 'AAOwner'],
        scope: [20, 12, 11], // 私密（20）受限（12）公开（11）
    },
    document: {
        roleName: ['DocReader', 'DocMember', 'DocAdmin'],
        repositoryRoleName: ['Reader', 'Member', 'Admin', 'AAOwner'],
        scope: [20, 5, 6], // 不公开（20）公开可查看（5）公开可编辑（6）
        inheritRepo: [2, 1], // 不继承知识库成员（2）继承成员（1）
    },
};

const KU_STATUS_LIST: any = {
    department: [-15],
    repository: [403, 404],
    document: [403, 404],
};

const KuAuthApiGetInfo: any = {
    department: () => {
        const params = getPageParams();
        if (!params || !params.spaceGuid || !params.groupGuid) {
            return;
        }
        getDepartmentInfo(params);
    },
    repository: () => {
        const params = store.getCurPage();
        if (!params || !params.spaceGuid || !params.groupGuid || !params.repositoryGuid) {
            return;
        }
        getRepositoryInfo(params);
    },
    document: () => {
        const params = store.getCurPage();
        if (!params || !params.spaceGuid || !params.groupGuid || !params.repositoryGuid || !params.docGuid) {
            return;
        }
        getDocumentInfo(params);
    },
};

class KuAuthority {
    documentAuth: null;
    repositoryAuth: null;
    departmentAuth: null;

    constructor() {
        // todo 目前权限是实时计算(随用随拿)，后面存到成员变量，store变化再重新计算，减少计算次数
        this.documentAuth = null;
        this.repositoryAuth = null;
        this.departmentAuth = null;
    }

    getStoreValue(type: string, keyName?: string) {
        const value = store.getData(KU_AUTH_TYPE[type].storeKey, false);
        return value && keyName ? value[keyName] : value;
    }

    getErrorValue(type: string, keyName?: string) {
        const value = store.getData(KU_AUTH_TYPE[type].errorKey, false);
        return value && keyName ? value[keyName] : value;
    }

    getAuthOriginValue(type: string, keyName?: string, sourceData?: any) {
        const key = keyName || 'roleName';
        if (sourceData) {
            return sourceData[key];
        }
        const authTypeKey = KU_AUTH_TYPE[type][key];
        return this.getStoreValue(type, authTypeKey || keyName);
    }

    // 单一权限验证函数，strictEqual是否严格判断相等，默认为false即大于请求权限即可
    isAuthed(authItem: AuthItem, sourceData?: any) {
        const {type, keyName = 'roleName', requestAuth, strictEqual = false} = authItem;
        const originValue = this.getAuthOriginValue(type, keyName, sourceData);
        if (requestAuth === undefined || originValue === undefined) {
            return null;
        }
        const authList = KU_AUTH_LIST[type][keyName];
        if (!authList || strictEqual) {
            return originValue === requestAuth;
        }
        const currentAuthIndex = authList.indexOf(originValue);
        const requestAuthIndex = authList.indexOf(requestAuth);
        if (currentAuthIndex === -1 || requestAuthIndex === -1) {
            return null;
        }
        return currentAuthIndex >= requestAuthIndex;
    }

    // 返回多个权限验证值
    isAuthedByKeys(authItems: AuthItem[], sourceData?: any) {
        return authItems.map(item => {
            return this.isAuthed(item, sourceData);
        });
    }

    // 对多个权限验证值取'or'逻辑
    getValueLogicalOr(values: any[]) {
        const trueValues = values.filter((item: boolean|null) => !!item);
        return !!trueValues.length;
    }

    // 对多个权限验证值取'and'逻辑
    getValueLogicalAnd(values: any[]) {
        const falseValues = values.filter((item: boolean|null) => !item);
        return !!falseValues.length;
    }

    isError({type, checkCode}: any) {
        const errorCode = this.getErrorValue(type, 'code');
        if (checkCode === undefined) {
            return errorCode !== undefined;
        }
        return errorCode === checkCode;
    }

    // eslint-disable-next-line complexity
    isAuthedByMap<Type extends keyof AuthMapType>(
        type: Type, mapName: keyof AuthMapType[Type], sourceData?: any, ...otherArgs: any
    ) {
        const authMap: AuthObjType = KuAuthMap[type][mapName];
        if (!authMap) {
            return false;
        }
        const fnParams: AuthFnParams = {};
        if (Object.prototype.toString.call(authMap.error) === '[object Object]') {
            const errorValue = this.isError(authMap.error);
            if (!authMap.fn) {
                return errorValue;
            }
            fnParams.errorValue = errorValue;
        }
        if (Array.isArray(authMap.error)) {
            const errorValues = authMap.error.map((item: any) => this.isError(item));
            if (authMap.condition === 'or') {
                return this.getValueLogicalOr(errorValues);
            } else if (authMap.condition === 'and') {
                return this.getValueLogicalAnd(errorValues);
            }
        }
        if (Object.prototype.toString.call(authMap.auth) === '[object Object]') {
            const authItem = authMap.auth as AuthItem;
            const authValue = this.isAuthed(authItem, sourceData);
            if (!authMap.fn) {
                return authValue;
            }
            fnParams.authValue = authValue;
            fnParams.originValue = this.getAuthOriginValue(authItem.type, authItem.keyName);
        }
        if (Array.isArray(authMap.auth)) {
            const authValues = this.isAuthedByKeys(authMap.auth, sourceData);
            if (authMap.condition === 'or') {
                return this.getValueLogicalOr(authValues);
            } else if (authMap.condition === 'and') {
                return this.getValueLogicalAnd(authValues);
            }
            fnParams.authValues = authValues;
            fnParams.originValues = authMap.auth.map((item: AuthItem) => {
                return this.getAuthOriginValue(item.type, item.keyName);
            });
        }
        if (authMap.authOrigin) {
            return this.getAuthOriginValue(type, 'roleName', sourceData);
        }
        if (authMap.errorOrigin) {
            return this.getErrorValue(type);
        }
        if (authMap.byOtherMaps) {
            fnParams.otherMapAuths = authMap.byOtherMaps?.map((item: any) => {
                return this.isAuthedByMap(item.type, item.mapName, sourceData);
            });
        }
        return authMap.fn ? authMap.fn(fnParams, sourceData, ...otherArgs) : false;
    }

    getAuth<Type extends keyof AuthMapType>(type: Type, sourceData?: any): AuthResultType {
        const authMap = KuAuthMap[type];
        type authKeys = keyof AuthResultType;
        const res: {
            [key in authKeys]?: any;
        } = {};
        for (const key in authMap) {
            if (authMap.hasOwnProperty(key)) {
                res[key as authKeys] = this.isAuthedByMap(type, key, sourceData);
            }
        }
        return res;
    }

    getAuthDocument(sourceData?: any) {
        return this.getAuth('document', sourceData);
    }

    getAuthRepository(sourceData?: any) {
        return this.getAuth('repository', sourceData);
    }

    getAuthDepartment(sourceData?: any) {
        return this.getAuth('department', sourceData);
    }

    getRoleName(type: string) {
        return this.getAuthOriginValue(type, 'roleName');
    }

    getStatus(type: string) {
        return this.getAuthOriginValue(type, 'status');
    }

    getScope(type: string) {
        return this.getAuthOriginValue(type, 'scope');
    }

    getErrorCode(type: string) {
        return this.getErrorValue(type, 'code');
    }

    isStatusValid(type: string) {
        const status = this.getStatus(type);
        return !KU_STATUS_LIST[type].includes(status);
    }

}

const KuAuthorityInstance = new KuAuthority();

export default KuAuthorityInstance;
